/**
 * Handles reorder redirects from the Customer Account UI extension.
 * Include this script on the cart page template.
 *
 * Simple orders:    type === 'simple' — adds items + _preSelectedAddress, redirects to /checkout
 *                   (or to /cart?view=shipping-step when the items need split shipping)
 * Multi-package:    has `packages` — rebuilds _csb-ch-state, redirects to /cart?view=shipping-step
 */

(function () {
  'use strict';

  // Read the raw query value: URLSearchParams form-decodes, turning a '+' from the base64
  // alphabet into a space that atob then strips, which shifts every following character.
  // decodeURIComponent handles percent-encoded blobs and leaves older raw ones untouched.
  const rawMatch = /[?&]account_reorder=([^&]*)/.exec(window.location.search);
  let encoded = rawMatch && rawMatch[1];
  if (encoded) {
    try { encoded = decodeURIComponent(encoded); } catch (e) { /* already raw base64 */ }
  }
  if (!encoded) return;

  // Other cart scripts redirect to ?view=multishipping, which would drop the payload
  // above before it is ever read. They skip that redirect while this flag is set.
  window.csbReorderInProgress = true;

  // ── Full-page loader ──────────────────────────────────────────────────────
  // 1. Hide <body> immediately (runs synchronously in <head>, before any paint)
  //    so the cart layout never flashes even for a single frame.
  // 2. Once the overlay div is attached to the DOM, remove the body-hiding rule —
  //    the white overlay then covers the page until the redirect fires.
  // Spinner style mirrors .cart-order__summary:after from theme.css.liquid.

  var hideBodyStyle = document.createElement('style');
  hideBodyStyle.textContent = 'body{visibility:hidden!important}';
  (document.head || document.documentElement).appendChild(hideBodyStyle);

  var loaderStyle = document.createElement('style');
  loaderStyle.textContent = [
    '#csb-reorder-overlay{position:fixed;inset:0;z-index:9999;background:#fff;display:flex;align-items:center;justify-content:center}',
    '#csb-reorder-spinner{width:80px;height:80px;border:12px solid #f3f3f3;border-top:12px solid rgb(var(--button-background-primary));border-radius:50%;animation:csb-reorder-spin 2s linear infinite}',
    '@keyframes csb-reorder-spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}',
  ].join('');
  (document.head || document.documentElement).appendChild(loaderStyle);

  var overlay = document.createElement('div');
  overlay.id = 'csb-reorder-overlay';
  overlay.innerHTML = '<div id="csb-reorder-spinner"></div>';

  // A fetch can settle before DOMContentLoaded, so hideLoader may run before attach:
  // without this flag the pending attach would re-show an unstyled overlay for good.
  var loaderDismissed = false;

  function showLoader() {
    function attach() {
      if (loaderDismissed) return;
      document.body.appendChild(overlay);
      // Overlay is now covering the page — safe to restore body visibility.
      if (hideBodyStyle.parentNode) hideBodyStyle.parentNode.removeChild(hideBodyStyle);
    }
    if (document.body) {
      attach();
    } else {
      document.addEventListener('DOMContentLoaded', attach);
    }
  }

  function hideLoader() {
    loaderDismissed = true;
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    if (loaderStyle.parentNode) loaderStyle.parentNode.removeChild(loaderStyle);
    if (hideBodyStyle.parentNode) hideBodyStyle.parentNode.removeChild(hideBodyStyle);
  }

  function fail(message, detail) {
    console.error('[reorder-from-account] ' + message, detail || '');
    window.csbReorderInProgress = false;
    hideLoader();

    var box = document.createElement('div');
    box.id = 'csb-reorder-error';
    box.setAttribute('role', 'alert');
    box.style.cssText =
      'margin:1rem auto;max-width:60rem;padding:1rem 1.25rem;border:1px solid #d72c0d;' +
      'border-radius:.5rem;background:#fff4f4;color:#8b1a1a;font-size:1rem;text-align:center';
    box.textContent = message;

    function place() {
      if (document.getElementById('csb-reorder-error')) return;
      document.body.insertBefore(box, document.body.firstChild);
    }
    if (document.body) place();
    else document.addEventListener('DOMContentLoaded', place);
  }

  showLoader();

  // ── Parse reorder data ────────────────────────────────────────────────────

  let reorderData;
  try {
    reorderData = JSON.parse(decodeURIComponent(escape(atob(encoded))));
  } catch (e) {
    fail('We could not read this reorder link. Please open the order again from your account.', e);
    return;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  function getStateCodeByName(countryCode, province, returnName) {
    return typeof window.getStateCodeByName === 'function'
      ? window.getStateCodeByName(countryCode, province, returnName)
      : province || '';
  }

  function getCountryNameByCode(code) {
    return typeof window.getCountryNameByCode === 'function'
      ? window.getCountryNameByCode(code)
      : code || '';
  }

  function formatAddress(address, full, lineBreak) {
    return typeof window.formatAddress === 'function'
      ? window.formatAddress(address, full, lineBreak)
      : [address.address1, address.city, address.zip].filter(Boolean).join(', ');
  }

  function getCookie(name) {
    return typeof window.csbGetCookie === 'function'
      ? window.csbGetCookie(name)
      : (document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)') || []).pop() || '';
  }

  // Maps CustomerAddress fields (from Customer Account API) to the internal address format.
  // territoryCode = ISO country code (e.g. "US"), zoneCode = province code (e.g. "TX")
  function mapAddress(address) {
    if (!address) return {};
    const countryCode = address.territoryCode || '';
    const provinceCode = address.zoneCode || address.province || '';
    return {
      firstname:    address.firstName || '',
      lastname:     address.lastName || '',
      company:      address.company || '',
      address1:     address.address1 || '',
      address2:     address.address2 || '',
      zip:          address.zip || '',
      city:         address.city || '',
      state:        provinceCode,
      state_full:   getStateCodeByName(countryCode, provinceCode, true) || provinceCode,
      country:      address.country || getCountryNameByCode(countryCode),
      countryCode:  countryCode,
      phone:        address.phoneNumber || address.phone || '',
      validation_status: true,
      id: '',
    };
  }

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let freeGift = null;
  try {
    freeGift = JSON.parse(document.getElementById('free_gift_config').textContent);
  } catch (e) { /* no free gift config on this page */ }

  function isFreeGiftVariant(variantId) {
    return !!freeGift && Number(variantId) === Number(freeGift.gift_variant);
  }

  function isAttachedGift(item) {
    return !!(item.properties && item.properties._attached_gift);
  }

  // Gift lines are never copied from the order: they are re-attached here the way snippets/gifts.liquid does it.
  function attachFreeGifts(cart) {
    if (!freeGift || !freeGift.gift_available) return cart;

    const gifts = (cart.items || [])
      .filter((item) => freeGift.product_variants.includes(item.variant_id))
      .map((item) => {
        const properties = { _attached_gift: item.key };
        if (freeGift.show_property) properties['Gift for'] = item.title;
        return {
          id: freeGift.gift_variant,
          quantity: freeGift.two_gifts ? item.quantity * 2 : item.quantity,
          properties: properties,
        };
      });

    if (!gifts.length) return cart;

    return fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: gifts }),
    })
      .then((r) => r.json())
      .then((added) => {
        if (isCartError(added)) return Promise.reject(added.description || added.message);
        return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      })
      .catch((e) => {
        console.error('[reorder-from-account] Free gift was not added', e);
        return cart;
      });
  }

  function hasSplitShipping() {
    return fetch(`${root}?section_id=split-shipping-status`)
      .then((r) => r.text())
      .then((html) => {
        const section = new DOMParser().parseFromString(html, 'text/html').getElementById('shopify-section-split-shipping-status');
        return !!section && section.textContent.trim() === 'true';
      })
      .catch(() => false);
  }

  function saveMultiCartState(cart, addressFor) {
    const newState = {
      token: cart.token || getCookie('cart'),
      carts: [],
      groups: [],
      summary_collapse: false,
      customer_email: null,
      coupons: null,
    };

    function createEntry(item, line, address) {
      return {
        address: address,
        formattedAddress: formatAddress(address, true, true),
        item_id: item.key,
        line: line,
        item: item,
        gift_message_status: false,
        shipping_type: '',
        onBackorder: !!(item.properties && item.properties['On Backorder']),
      };
    }

    cart.items.forEach((item, i) => {
      if (!isAttachedGift(item)) newState.carts.push(createEntry(item, i, addressFor(item)));
    });

    cart.items.forEach((item, i) => {
      if (!isAttachedGift(item)) return;
      const parent = newState.carts.find((entry) => entry.item_id === item.properties._attached_gift);
      newState.carts.push(createEntry(item, i, parent ? parent.address : addressFor(item)));
    });

    window.multiCart = newState;
    localStorage.setItem('_csb-ch-state', JSON.stringify(newState));
  }

  // Under customer service the cart always lives in the multishipping view, so a rebuilt
  // order has to land there instead of checkout — otherwise the rep loses the CS controls.
  function destinationFor(fallback) {
    return getCookie('customerService')
      ? (window.marketPrefix || '') + '/cart?view=multishipping'
      : fallback;
  }

  const UNAVAILABLE_MESSAGE =
    'We could not rebuild this order — one or more items are no longer available. Your cart was left unchanged.';

  function isCartError(response) {
    if (!response) return true;
    if (response.message === 'Cart Error') return true;
    return !!response.status && String(response.status) !== '200';
  }

  // Distinguishes cart lines that share a variant but belong to different packages.
  function lineSignature(variantId, properties) {
    const reorderId = properties && properties._reorder_id;
    return String(variantId) + '|' + (reorderId || '');
  }

  // Adds the reorder items first, then trims whatever else is in the cart. Adding before
  // removing is what keeps a rejected item from leaving the customer with an empty cart.
  function replaceCartWith(items, extra) {
    return fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign({ items: items }, extra || {})),
    })
      .then((r) => r.json())
      .then((added) => {
        if (isCartError(added)) {
          return Promise.reject(added.description || added.message || 'Cart Error');
        }
        return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      })
      .then((cart) => {
        const wanted = {};
        items.forEach((item) => {
          const key = lineSignature(item.id, item.properties);
          wanted[key] = (wanted[key] || 0) + item.quantity;
        });

        const updates = {};
        let changed = false;
        (cart.items || []).forEach((line) => {
          const key = lineSignature(line.variant_id, line.properties);
          let target = 0;
          if (wanted[key] != null) {
            target = wanted[key];
            delete wanted[key];
          }
          updates[line.key] = target;
          if (target !== line.quantity) changed = true;
        });

        if (!changed) return cart;

        return fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates }),
        })
          .then((r) => r.json())
          .then((updated) => (isCartError(updated) ? cart : updated));
      });
  }

  // ── Simple order ──────────────────────────────────────────────────────────

  function handleSimpleReorder(items, shippingAddress) {
    if (!items || !items.length) {
      fail(UNAVAILABLE_MESSAGE, 'No items for simple reorder');
      return;
    }

    const cartItems = items
      .filter((item) => item.variantId && !isFreeGiftVariant(item.variantId))
      .map((item) => ({ id: item.variantId, quantity: item.quantity }));

    if (!cartItems.length) {
      fail(UNAVAILABLE_MESSAGE, 'No line item carried a variant id');
      return;
    }

    const mapped = mapAddress(shippingAddress);

    replaceCartWith(cartItems, {
      attributes: {
        _preSelectedAddress: {
          firstname:   mapped.firstname,
          lastname:    mapped.lastname,
          company:     mapped.company,
          address1:    mapped.address1,
          address2:    mapped.address2,
          zip:         mapped.zip,
          city:        mapped.city,
          state:       mapped.state,
          country:     mapped.country,
          countryCode: mapped.countryCode,
          phone:       mapped.phone,
        },
      },
    })
      .then(attachFreeGifts)
      .then((cart) => hasSplitShipping().then((splitShipping) => {
        if (!splitShipping) {
          window.location.href = destinationFor((window.marketPrefix || '') + '/checkout');
          return;
        }

        saveMultiCartState(cart, () => mapped);
        window.location.href = destinationFor((window.marketPrefix || '') + '/cart?view=shipping-step');
      }))
      .catch((e) => { fail(UNAVAILABLE_MESSAGE, e); });
  }

  // ── Multi-package order ───────────────────────────────────────────────────

  function handleMultiReorder(packages, shippingAddress, packageAddresses) {
    if (!packages || !Object.keys(packages).length) {
      fail(UNAVAILABLE_MESSAGE, 'No package data found');
      return;
    }

    const allItems = [];
    Object.values(packages).forEach((pkgItems) => {
      pkgItems.forEach((item) => {
        if (!item.variantId || isFreeGiftVariant(item.variantId)) return;
        const mappedItem = {
          id: item.variantId,
          quantity: item.quantity,
          properties: { _reorder_id: item.reorderId || '' },
        };
        if (item.duplicate) mappedItem.properties['_duplicate'] = item.duplicate;
        allItems.push(mappedItem);
      });
    });

    if (!allItems.length) {
      fail(UNAVAILABLE_MESSAGE, 'No items to add to cart');
      return;
    }

    replaceCartWith(allItems)
      .then(attachFreeGifts)
      .then((cart) => {
        const cartLineItems = cart.items;
        if (!cartLineItems || !cartLineItems.length) {
          fail(UNAVAILABLE_MESSAGE, 'Cart came back empty after adding items');
          return;
        }

        saveMultiCartState(cart, (item) => {
          const reorderId = item.properties && item.properties._reorder_id;
          let pkgNum = null;

          // Prefer exact reorderId match; variantId fallback only when no reorderId match found.
          // forEach without break causes variantId to overwrite a correct reorderId match when
          // two packages share the same variant — so we use explicit loops with early exit.
          if (reorderId) {
            for (const [num, pkgItems] of Object.entries(packages)) {
              if (pkgItems.find((p) => String(p.reorderId) === String(reorderId))) {
                pkgNum = num;
                break;
              }
            }
          }
          if (pkgNum === null) {
            for (const [num, pkgItems] of Object.entries(packages)) {
              if (pkgItems.find((p) => String(p.variantId) === String(item.variant_id))) {
                pkgNum = num;
                break;
              }
            }
          }

          // Use per-package address from the child order; fall back to parent shippingAddress
          const rawAddress =
            (pkgNum !== null && packageAddresses && packageAddresses[pkgNum])
              ? packageAddresses[pkgNum]
              : shippingAddress || {};
          return mapAddress(rawAddress);
        });

        window.location.href = destinationFor('/cart?view=shipping-step');
      })
      .catch((e) => { fail(UNAVAILABLE_MESSAGE, e); });
  }

  // ── Route by type ─────────────────────────────────────────────────────────

  const { type, packages, items, shippingAddress, packageAddresses } = reorderData;

  if (type === 'simple') {
    handleSimpleReorder(items, shippingAddress);
  } else {
    handleMultiReorder(packages, shippingAddress, packageAddresses || {});
  }
})();
